import { injToken } from '@shared/data/token'
import { Coin, MsgExecuteContractCompat } from '@injectivelabs/sdk-ts'
import { msgBroadcaster } from '@shared/WalletService'
import { sharedToBalanceInWei } from '@shared/utils/formatter'
import {
  CPMM_CONTRACT_CODE,
  ASMM_SPOT_CONTRACT_CODE,
  ASMM_PERP_CONTRACT_CODE,
  BASIS_POINT_DECIMAL_PLACES,
  MITO_MASTER_CONTRACT_ADDRESS,
  MASTER_CONTRACT_OWNER_ADDRESS
} from '@/app/utils/constant'
import {
  formatBaseConfig,
  formatMarketMarkingConfig,
  formatMsgPricingStrategyAmount
} from '@/app/client/utils/vaultConfig/formatter'
import { delayPromiseCall } from '@/app/client/utils/async'
import {
  CPMMConfigField,
  VaultSpotConfig,
  VaultConfigForm,
  VaultBaseConfig,
  VaultConfigField,
  VaultDerivativeConfig
} from '@/types'

export const instantiatePermissionlessCPMMVault = async ({
  funds,
  formValues
}: {
  funds: Coin[]
  formValues: VaultConfigForm
}) => {
  const mitoStore = useMitoStore()
  const walletStore = useWalletStore()
  const sharedWalletStore = useSharedWalletStore()

  if (!sharedWalletStore.isUserConnected) {
    return
  }

  await walletStore.validateAndQueue()

  const msgs = MsgExecuteContractCompat.fromJSON({
    contractAddress: MITO_MASTER_CONTRACT_ADDRESS,
    funds,
    exec: {
      action: 'register_vault',
      msg: {
        is_subscribing_with_funds: true,
        registration_mode: {
          permissionless: {
            whitelisted_vault_code_id: CPMM_CONTRACT_CODE
          }
        },
        instantiate_vault_msg: {
          Amm: {
            ...formatBaseConfig({
              formValues,
              contractAddress: MITO_MASTER_CONTRACT_ADDRESS,
              config: {
                ...(mitoStore.vaultConfig.base as VaultBaseConfig),
                owner: sharedWalletStore.injectiveAddress
              }
            }),
            pricing_strategy: formatMsgPricingStrategyAmount({ formValues }),
            max_invariant_sensitivity_bps: sharedToBalanceInWei({
              value: formValues[CPMMConfigField.MaxInvariantSensitivityBps],
              decimalPlaces: BASIS_POINT_DECIMAL_PLACES
            }).toFixed(),
            max_price_sensitivity_bps: sharedToBalanceInWei({
              value: formValues[CPMMConfigField.MaxPriceSensitivityBps],
              decimalPlaces: BASIS_POINT_DECIMAL_PLACES
            }).toFixed(),
            fee_bps: sharedToBalanceInWei({
              value: formValues[CPMMConfigField.FeeBps],
              decimalPlaces: BASIS_POINT_DECIMAL_PLACES
            }).toNumber(),
            order_type: formValues[CPMMConfigField.OrderType],
            config_owner: sharedWalletStore.injectiveAddress,
            base_decimals: Number(formValues[VaultConfigField.BaseDecimals]),
            quote_decimals: Number(formValues[VaultConfigField.QuoteDecimals])
          }
        }
      }
    },
    sender: sharedWalletStore.injectiveAddress
  })

  await msgBroadcaster.broadcastV2({
    injectiveAddress: sharedWalletStore.injectiveAddress,
    msgs
  })

  await delayPromiseCall(mitoStore.fetchVaults, 3000)
}

export const instantiateCPMMVault = async (formValues: VaultConfigForm) => {
  const mitoStore = useMitoStore()
  const paramStore = useParamStore()
  const walletStore = useWalletStore()
  const sharedWalletStore = useSharedWalletStore()

  if (!sharedWalletStore.isUserConnected) {
    return
  }

  await walletStore.validateAndQueue()

  const msgs = MsgExecuteContractCompat.fromJSON({
    contractAddress: MITO_MASTER_CONTRACT_ADDRESS,
    funds: [
      {
        denom: injToken.denom,
        amount: paramStore.lpTokenCreationFee
      }
    ],
    exec: {
      action: 'register_vault',
      msg: {
        is_subscribing_with_funds: false,
        registration_mode: {
          restricted: {
            vault_code_id: CPMM_CONTRACT_CODE,
            vault_label: formValues[VaultConfigField.Label],
            vault_upgrade_admin: MASTER_CONTRACT_OWNER_ADDRESS
          }
        },
        instantiate_vault_msg: {
          Amm: {
            ...formatBaseConfig({
              formValues,
              contractAddress: MITO_MASTER_CONTRACT_ADDRESS,
              config: {
                ...(mitoStore.vaultConfig.base as VaultBaseConfig),
                owner: sharedWalletStore.injectiveAddress
              }
            }),
            pricing_strategy: formatMsgPricingStrategyAmount({ formValues }),
            max_invariant_sensitivity_bps: sharedToBalanceInWei({
              value: formValues[CPMMConfigField.MaxInvariantSensitivityBps],
              decimalPlaces: BASIS_POINT_DECIMAL_PLACES
            }).toFixed(),
            max_price_sensitivity_bps: sharedToBalanceInWei({
              value: formValues[CPMMConfigField.MaxPriceSensitivityBps],
              decimalPlaces: BASIS_POINT_DECIMAL_PLACES
            }).toFixed(),
            fee_bps: sharedToBalanceInWei({
              value: formValues[CPMMConfigField.FeeBps],
              decimalPlaces: BASIS_POINT_DECIMAL_PLACES
            }).toNumber(),
            order_type: formValues[CPMMConfigField.OrderType],
            // instantiate only
            config_owner: sharedWalletStore.injectiveAddress,
            base_decimals: Number(formValues[VaultConfigField.BaseDecimals]),
            quote_decimals: Number(formValues[VaultConfigField.QuoteDecimals])
          }
        }
      }
    },
    sender: sharedWalletStore.injectiveAddress
  })

  await msgBroadcaster.broadcastV2({
    injectiveAddress: sharedWalletStore.injectiveAddress,
    msgs
  })

  await delayPromiseCall(mitoStore.fetchVaults, 3000)
}

export const instantiateSpotVault = async ({
  formValues
}: {
  formValues: VaultConfigForm
}) => {
  const mitoStore = useMitoStore()
  const paramStore = useParamStore()
  const walletStore = useWalletStore()
  const sharedWalletStore = useSharedWalletStore()

  if (!sharedWalletStore.isUserConnected) {
    return
  }

  await walletStore.validateAndQueue()

  const msgs = MsgExecuteContractCompat.fromJSON({
    contractAddress: MITO_MASTER_CONTRACT_ADDRESS,
    funds: [
      {
        denom: injToken.denom,
        amount: paramStore.lpTokenCreationFee
      }
    ],
    exec: {
      action: 'register_vault',
      msg: {
        is_subscribing_with_funds: false,
        registration_mode: {
          restricted: {
            vault_code_id: ASMM_SPOT_CONTRACT_CODE,
            vault_label: formValues[VaultConfigField.Label],
            vault_upgrade_admin: MASTER_CONTRACT_OWNER_ADDRESS
          }
        },
        instantiate_vault_msg: {
          Spot: {
            ...formatBaseConfig({
              formValues,
              contractAddress: MITO_MASTER_CONTRACT_ADDRESS,
              config: {
                ...(mitoStore.vaultConfig.base as VaultBaseConfig),
                owner: sharedWalletStore.injectiveAddress
              }
            }),
            ...formatMarketMarkingConfig({
              formValues,
              config: (mitoStore.vaultConfig as VaultSpotConfig).marketMaking
            }),
            oracle_type: Number(formValues[VaultConfigField.OracleType]),
            target_base_weight: formValues[VaultConfigField.TargetBaseWeight],
            allowed_redemption_types: Number(
              formValues[VaultConfigField.AllowedSpotRedemptionType]
            ),
            // instantiate only
            config_owner: sharedWalletStore.injectiveAddress,
            base_decimals: Number(formValues[VaultConfigField.BaseDecimals]),
            quote_decimals: Number(formValues[VaultConfigField.QuoteDecimals]),
            base_oracle_symbol: formValues[VaultConfigField.BaseOracleSymbol],
            quote_oracle_symbol: formValues[VaultConfigField.QuoteOracleSymbol]
          }
        }
      }
    },
    sender: sharedWalletStore.injectiveAddress
  })

  await msgBroadcaster.broadcastV2({
    injectiveAddress: sharedWalletStore.injectiveAddress,
    msgs
  })
}

export const instantiateDerivativeVault = async ({
  formValues
}: {
  formValues: VaultConfigForm
}) => {
  const mitoStore = useMitoStore()
  const paramStore = useParamStore()
  const walletStore = useWalletStore()
  const sharedWalletStore = useSharedWalletStore()

  if (!sharedWalletStore.isUserConnected) {
    return
  }

  await walletStore.validateAndQueue()

  const msgs = MsgExecuteContractCompat.fromJSON({
    contractAddress: MITO_MASTER_CONTRACT_ADDRESS,
    funds: [
      {
        denom: injToken.denom,
        amount: paramStore.lpTokenCreationFee
      }
    ],
    exec: {
      action: 'register_vault',
      msg: {
        is_subscribing_with_funds: false,
        registration_mode: {
          restricted: {
            vault_code_id: ASMM_PERP_CONTRACT_CODE,
            vault_label: formValues[VaultConfigField.Label],
            vault_upgrade_admin: MASTER_CONTRACT_OWNER_ADDRESS
          }
        },
        instantiate_vault_msg: {
          Derivative: {
            ...formatBaseConfig({
              formValues,
              contractAddress: MITO_MASTER_CONTRACT_ADDRESS,
              config: {
                ...(mitoStore.vaultConfig.base as VaultBaseConfig),
                owner: sharedWalletStore.injectiveAddress
              }
            }),
            ...formatMarketMarkingConfig({
              formValues,
              config: (mitoStore.vaultConfig as VaultDerivativeConfig)
                .marketMaking
            }),
            leverage: formValues[VaultConfigField.Leverage],
            min_proximity_to_liquidation:
              formValues[VaultConfigField.MinProximityToLiquidation],
            allowed_redemption_types: Number(
              formValues[VaultConfigField.AllowedDerivativeRedemptionType]
            ),
            // instantiate only
            config_owner: sharedWalletStore.injectiveAddress,
            position_pnl_penalty:
              formValues[VaultConfigField.PositionPnLPenalty]
          }
        }
      }
    },
    sender: sharedWalletStore.injectiveAddress
  })

  await msgBroadcaster.broadcastV2({
    injectiveAddress: sharedWalletStore.injectiveAddress,
    msgs
  })
}
